import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";

const { RangePicker } = DatePicker;

export type RangeValue = [Dayjs | null, Dayjs | null];

interface CustomRangePickerProps {
  onChange: (dates: RangeValue) => void;
  value?: RangeValue;
  allowClear?: boolean;
  style?: React.CSSProperties;
  placeholder?: [string, string];
  size?: "small" | "middle" | "large";
  className?: string;
  disabled?: boolean;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

const getThisMonth = (): [Dayjs, Dayjs] => {
  const now = dayjs();
  return [now.startOf("month"), now.endOf("day")];
};

const getLastMonth = (): [Dayjs, Dayjs] => {
  const now = dayjs().subtract(1, "month");
  return [now.startOf("month"), now.endOf("month")];
};

const getQuarterRange = (isPrevious = false): [Dayjs, Dayjs] => {
  const now = dayjs();
  let currentQuarter = Math.floor(now.month() / 3);
  let year = now.year();
  if (isPrevious) {
    currentQuarter -= 1;
    if (currentQuarter < 0) {
      currentQuarter = 3;
      year -= 1;
    }
  }
  const startMonth = currentQuarter * 3;
  const start = dayjs().year(year).month(startMonth).startOf("month");
  const end = isPrevious
    ? dayjs()
        .year(year)
        .month(startMonth + 2)
        .endOf("month")
    : dayjs();
  return [start, end];
};

const getThisYear = (): [Dayjs, Dayjs] => {
  const now = dayjs();
  return [now.startOf("year"), now.endOf("day")];
};

const getLastYear = (): [Dayjs, Dayjs] => {
  const now = dayjs().subtract(1, "year");
  return [now.startOf("year"), now.endOf("year")];
};

const isSameRange = (range1: RangeValue, range2: [Dayjs, Dayjs]) => {
  if (!range1[0] || !range1[1]) return false;

  return (
    range1[0].isSame(range2[0], "day") && range1[1].isSame(range2[1], "day")
  );
};

const getActivePreset = (value?: RangeValue) => {
  if (!value) return undefined;

  if (isSameRange(value, getThisMonth())) return "This Month";
  if (isSameRange(value, getLastMonth())) return "Last Month";
  if (isSameRange(value, getQuarterRange(false))) return "This Quarter";
  if (isSameRange(value, getQuarterRange(true))) return "Last Quarter";
  if (isSameRange(value, getThisYear())) return "This Year";
  if (isSameRange(value, getLastYear())) return "Last Year";

  return undefined;
};

const CustomRangePicker: React.FC<CustomRangePickerProps> = ({
  onChange,
  value,
  allowClear = true,
  style,
  placeholder = ["From Date", "To Date"],
  size,
  className,
  disabled,
  placement,
  getPopupContainer,
}) => {
  const activePreset = getActivePreset(value);
  return (
    <RangePicker
      allowClear={allowClear}
      placeholder={placeholder}
      style={{ width: "100%", ...style }}
      format="DD/MM/YYYY"
      value={value}
      size={size}
      className={className}
      disabled={disabled}
      placement={placement}
      getPopupContainer={getPopupContainer}
      onChange={(dates) => onChange([dates?.[0] || null, dates?.[1] || null])}
      presets={[
        {
          label:
            activePreset === "This Month" ? <b>This Month</b> : "This Month",
          value: getThisMonth(),
        },
        {
          label:
            activePreset === "Last Month" ? <b>Last Month</b> : "Last Month",
          value: getLastMonth(),
        },
        {
          label:
            activePreset === "This Quarter" ? (
              <b>This Quarter</b>
            ) : (
              "This Quarter"
            ),
          value: getQuarterRange(false),
        },
        {
          label:
            activePreset === "Last Quarter" ? (
              <b>Last Quarter</b>
            ) : (
              "Last Quarter"
            ),
          value: getQuarterRange(true),
        },
        {
          label: activePreset === "This Year" ? <b>This Year</b> : "This Year",
          value: getThisYear(),
        },
        {
          label: activePreset === "Last Year" ? <b>Last Year</b> : "Last Year",
          value: getLastYear(),
        },
      ]}
    />
  );
};

export default CustomRangePicker;
